/**
 * Audio format utilities for cross-browser compatibility
 * Uses music-metadata for robust metadata extraction
 */

import { IAudioMetadata, parseBlob } from 'music-metadata';

/**
 * MIME types to try in priority order for MediaRecorder.
 * - Safari/iOS picks the first MP4 entry (AAC-LC explicitly tagged).
 * - Firefox picks audio/webm;codecs=opus (it has no MP4 muxer for MediaRecorder).
 * - Chrome accepts webm/opus or webm.
 * Backend accepts all of these (see encounter_service.ALLOWED_AUDIO_TYPES).
 */
export const SUPPORTED_MIME_TYPES = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/wav',
] as const;

/**
 * Audio metadata extracted from file
 */
export interface AudioMetadata {
  /** Bitrate in bits per second */
  bitrate?: number;
  /** Number of channels */
  channels?: number;
  /** Codec (e.g., 'mp3', 'aac') */
  codec?: string;
  /** Duration in milliseconds */
  duration: number;
  /** File format (e.g., 'MP3', 'MP4', 'WAV') */
  format?: string;
  /** Sample rate in Hz */
  sampleRate?: number;
}

/**
 * Result of audio metadata extraction
 */
export interface AudioMetadataResult {
  /** Original or processed blob */
  blob: Blob;
  /** Extracted metadata */
  metadata: AudioMetadata;
}

/**
 * Extract audio metadata from a blob using music-metadata
 * Single Responsibility: Only extracts metadata, doesn't transform
 * @param blob - Audio blob to analyze
 * @returns Promise with metadata
 * @throws Error if blob is not a valid audio file
 */
export async function extractAudioMetadata(blob: Blob): Promise<AudioMetadata> {
  try {
    const metadata: IAudioMetadata = await parseBlob(blob);

    return {
      codec: metadata.format.codec,
      bitrate: metadata.format.bitrate,
      sampleRate: metadata.format.sampleRate,
      channels: metadata.format.numberOfChannels,
      duration: (metadata.format.duration || 0) * 1000, // Convert to milliseconds
      format: metadata.format.container?.toUpperCase(),
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Failed to extract audio metadata: ${errorMessage}`);
  }
}

/**
 * Get audio metadata with the original blob
 * Open/Closed: Extensible through AudioMetadata interface
 * @param blob - Audio blob to process
 * @returns Promise with blob and metadata
 */
export async function getAudioMetadata(blob: Blob): Promise<AudioMetadataResult> {
  const metadata = await extractAudioMetadata(blob);

  return {
    blob,
    metadata,
  };
}

/**
 * Validate if a blob is a supported audio format
 * @param blob - Blob to validate
 * @returns true if blob appears to be audio
 */
export function isAudioBlob(blob: Blob): boolean {
  return (
    blob.type.startsWith('audio/') ||
    blob.type === 'video/webm' || // WebM from MediaRecorder can be video/webm even for audio-only
    blob.type === ''
  );
}

/**
 * True when the MediaRecorder API is available in this environment.
 * Use to gate the recording feature before calling pickRecorderMimeType().
 */
export function isMediaRecorderSupported(): boolean {
  return typeof MediaRecorder !== 'undefined';
}

/**
 * Return the first SUPPORTED_MIME_TYPES entry MediaRecorder accepts on this
 * browser, or '' if none. Callers should treat '' as "feature unsupported".
 */
export function pickRecorderMimeType(): string {
  if (!isMediaRecorderSupported()) {
    return '';
  }
  for (const type of SUPPORTED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return '';
}
